import React from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { TranslationKeys } from "translations";
import { StyleEnums } from "models/enums";
import { IconDefinitionConstants } from "utils/constants";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
//#endregion
//#region components
import { PageLayouts, RouteBreadCrumb, Skeletons, StaticInfoMessage, TableAsync, ViewPanel } from "components/common";
import CopyButton from "components/common/buttons/copyButton";
import useCompanyInvoiceMail from "components/common/useCompanyInvoiceMail";
import { Grid } from "@mui/material";
import useAdvisorPermissions from "./usePermissions";
import { useCompanyService } from "serviceHooks";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

type Row = WintSuperkollAPIModel.PersonEmailAddresses;

// A company has at most a few hundred users, so the whole set is one page (see monthlyReports/list.tsx).
const SINGLE_PAGE_ROW_COUNT = 1000;

const copyable = (address?: string) =>
  address ? (
    <CopyButton copyText={address} showCopyIcon>
      {address}
    </CopyButton>
  ) : (
    "—"
  );

/**
 * Every Wint inbound address on the company in one place, so the advisor can answer "where do I send
 * this?" for any user: the company's supplier-invoice address, and each user's document and self invoice
 * addresses. Mounted as a sub-page of Rådgivningsvyn, reached from the shortcuts widget.
 */
const EmailAddresses: React.FC = () => {
  const { advisorViewsAvailable } = useAdvisorPermissions();
  const { getEmailAddresses } = useCompanyService();
  const companyInvoiceMail = useCompanyInvoiceMail();
  const selectedCompanyId = useSelector((store: AppState) => store.auth.selectedCompanyId);

  const [persons, setPersons] = React.useState<Row[]>();
  const [loadFailed, setLoadFailed] = React.useState<boolean>(false);

  // Reload on company switch, like useCompanyInvoiceMail, so the list never shows the previous company's people.
  React.useEffect(() => {
    setPersons(undefined);
    setLoadFailed(false);
    let cancelled = false;
    getEmailAddresses()
      .then((result) => !cancelled && setPersons(result))
      .catch(() => !cancelled && setLoadFailed(true));
    return () => {
      cancelled = true;
    };
    // `getEmailAddresses` is fresh each render, so including it would loop.
  }, [selectedCompanyId]);

  // Core returns selfInvoiceEmail only when self invoicing is enabled, so the rows decide the column.
  const hasSelfInvoiceEmails = persons?.some((p) => p.selfInvoiceEmail) ?? false;

  const title = LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.EmailAddresses);

  return (
    <PageLayouts.DashboardLayout
      entityKey={0}
      hasPermissions={advisorViewsAvailable}
      title={title}
      breadCrumb={<RouteBreadCrumb className={cssExports.dashBoardNavigation} current={title} />}
    >
      <Grid container item direction="column" spacing={2}>
        {/* Hidden when the company has no company-specific supplier-invoice address (or it couldn't be read). */}
        {companyInvoiceMail && (
          <Grid item>
            <ViewPanel
              icon={IconDefinitionConstants.WSupplierInvoice}
              title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.SupplierInvoiceEmail)}
            >
              <Grid
                container
                direction="row"
                alignItems="center"
                columnGap={0.5}
                className={cssExports.formControlDescription}
              >
                {LocalizationHelperInstance.getFormattedMessage(
                  TranslationKeys.IncomingInvoice.CompanyInvoiceMailLabel
                )}
                {copyable(companyInvoiceMail)}
              </Grid>
            </ViewPanel>
          </Grid>
        )}
        <Grid item>
          {loadFailed ? (
            <StaticInfoMessage displayType={StyleEnums.DisplayType.Danger}>
              {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.EmailAddressesLoadFailed)}
            </StaticInfoMessage>
          ) : persons === undefined ? (
            <Skeletons.TableSkeleton />
          ) : (
            <TableAsync<Row>
              tableId="advisorEmailAddresses"
              filter={persons}
              maxRowCount={SINGLE_PAGE_ROW_COUNT}
              disableQueryPagination
              orderByProperty="name"
              properties={{
                name: { description: TranslationKeys.Common.Name, noWrap: true },
                documentEmail: {
                  description: TranslationKeys.Advisor.DocumentEmail,
                  helpTooltip: TranslationKeys.Advisor.DocumentEmailTooltip,
                  noWrap: true,
                  disableSort: true,
                  formatter: (r: Row) => copyable(r.documentEmail),
                },
                ...(hasSelfInvoiceEmails && {
                  selfInvoiceEmail: {
                    description: TranslationKeys.Advisor.SelfInvoiceEmail,
                    helpTooltip: TranslationKeys.Advisor.SelfInvoiceEmailTooltip,
                    noWrap: true,
                    disableSort: true,
                    formatter: (r: Row) => copyable(r.selfInvoiceEmail),
                  },
                }),
              }}
            />
          )}
        </Grid>
      </Grid>
    </PageLayouts.DashboardLayout>
  );
};

export default EmailAddresses;
